import { useState, useCallback, useRef } from 'react';

import { useTranslations } from 'next-intl';
import {
  parseUnits,
  type Hash,
  type Address,
  UserRejectedRequestError,
  BaseError,
  type Abi
} from 'viem';
import {
  useAccount,
  useReadContract,
  useWriteContract,
  usePublicClient,
  useChainId
} from 'wagmi';

import { poolContractAbi, erc20Abi } from '@/abi';
import { removeCommas } from '@/lib/utils/removeCommas';
import { C_Regexp } from '@/ts/constants/C_Regexp';

// Proper user rejection detection using viem error types
const isUserRejection = (error: unknown): boolean => {
  if (error instanceof UserRejectedRequestError) return true;

  if (error instanceof BaseError) {
    const cause = error.cause;
    if (cause && typeof cause === 'object') {
      const causeStr = String(cause).toLowerCase();
      return (
        causeStr.includes('user rejected') ||
        causeStr.includes('user denied') ||
        causeStr.includes('rejected') ||
        causeStr.includes('denied') ||
        causeStr.includes('cancelled') ||
        causeStr.includes('canceled')
      );
    }
  }
  return false;
};

async function checkPaused(
  publicClient: ReturnType<typeof usePublicClient>,
  address?: Address,
  poolAbi?: Abi
): Promise<boolean | null> {
  if (!publicClient || !address || !poolAbi) return null;

  try {
    const p = await publicClient.readContract({
      address,
      abi: poolAbi,
      functionName: 'paused'
    });
    if (typeof p === 'boolean') return p;
  } catch {
    return null;
  }

  return null;
}

export type T_SellTokensState = {
  isLoading: boolean;
  isApproving: boolean;
  isSelling: boolean;
  error?: string;
  txHash?: Hash;
};

export type T_SellTokensResult = {
  success: boolean;
  txHash?: Hash;
  error?: string;
};

export function useSellTokens(
  poolContractAddress: Address,
  tokenAddress: Address,
  tokenDecimals: number
) {
  const t = useTranslations('pools');
  const poolAddress = poolContractAddress;
  const { address } = useAccount();
  const chainId = useChainId();
  const publicClient = usePublicClient();
  const { writeContractAsync } = useWriteContract();

  const [state, setState] = useState<T_SellTokensState>({
    isLoading: false,
    isApproving: false,
    isSelling: false
  });

  const pauseStateRef = useRef<{ token: boolean | null; pool: boolean | null }>(
    { token: null, pool: null }
  );

  const isProcessingRef = useRef(false);

  const POOL_ABI = poolContractAbi;
  const ERC20_ABI = erc20Abi;

  // Get token balance
  const { data: tokenBalance, refetch: refetchTokenBalance } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: !!address && !!tokenAddress }
  }) as { data: bigint | undefined; refetch: () => Promise<unknown> };

  // Get token allowance for pool contract
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args:
      address && poolAddress && tokenAddress
        ? [address, poolAddress]
        : undefined,
    query: { enabled: !!address && !!tokenAddress }
  }) as { data: bigint | undefined; refetch: () => Promise<unknown> };

  const sellTokens = useCallback(
    async (tokenAmountInput: string): Promise<T_SellTokensResult> => {
      if (isProcessingRef.current) {
        return { success: false, error: t('inProgress') };
      }

      if (!address) return { success: false, error: t('walletNotConnected') };
      if (!chainId) return { success: false, error: t('wrongNetwork') };

      const tokenAmountWithoutCommas = removeCommas(tokenAmountInput);

      const poolPaused = await checkPaused(publicClient, poolAddress, POOL_ABI);
      pauseStateRef.current = { token: null, pool: poolPaused };

      if (poolPaused === true) {
        return { success: false, error: t('poolPaused') };
      }

      isProcessingRef.current = true;
      setState(prev => ({ ...prev, isLoading: true, error: undefined }));

      try {
        const tokenAmountBigInt = parseUnits(tokenAmountWithoutCommas, 18);
        // Get fresh balance and allowance
        const [freshBalance, freshAllowance] = await Promise.all([
          (publicClient?.readContract({
            address: tokenAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [address]
          }) as Promise<bigint>) || 0n,
          (publicClient?.readContract({
            address: tokenAddress,
            abi: ERC20_ABI,
            functionName: 'allowance',
            args: [address, poolAddress]
          }) as Promise<bigint>) || 0n
        ]);

        if (freshBalance < tokenAmountBigInt) {
          throw new Error(t('insufficientBalance'));
        }

        // Approve token if needed
        if (freshAllowance < tokenAmountBigInt) {
          setState(prev => ({ ...prev, isApproving: true }));

          const simulateApprove = await publicClient
            ?.simulateContract({
              address: tokenAddress,
              abi: ERC20_ABI,
              functionName: 'approve',
              args: [poolAddress, tokenAmountBigInt],
              account: address
            })
            .catch(() => null);
          if (!simulateApprove) throw new Error(t('allowanceFailed'));

          const approveHash = await writeContractAsync(simulateApprove.request);
          await publicClient?.waitForTransactionReceipt({ hash: approveHash });
          setState(prev => ({ ...prev, isApproving: false }));
          try {
            await refetchAllowance();
          } catch {}
        }

        // Execute sell
        const simulateSell = await publicClient
          ?.simulateContract({
            address: poolAddress,
            abi: POOL_ABI,
            functionName: 'sellTokens',
            args: [tokenAmountBigInt],
            account: address
          })
          .catch(() => null);
        if (!simulateSell) throw new Error(t('sellFailed'));

        setState(prev => ({ ...prev, isSelling: true }));

        const sellHash = await writeContractAsync(simulateSell.request);
        await publicClient?.waitForTransactionReceipt({ hash: sellHash });

        try {
          await refetchTokenBalance();
        } catch {}
        try {
          await refetchAllowance();
        } catch {}

        setState(prev => ({
          ...prev,
          isLoading: false,
          isSelling: false,
          txHash: sellHash
        }));
        return { success: true, txHash: sellHash };
      } catch (e) {
        const msg = e instanceof Error ? e.message : t('unknownError');
        const userRejected = isUserRejection(e);
        const errorMessage = userRejected ? t('transactionRejected') : msg;

        setState(prev => ({
          ...prev,
          isLoading: false,
          isApproving: false,
          isSelling: false,
          error: errorMessage
        }));
        return { success: false, error: errorMessage };
      } finally {
        isProcessingRef.current = false;
      }
    },
    [
      address,
      chainId,
      t,
      tokenAddress,
      tokenDecimals,
      publicClient,
      ERC20_ABI,
      poolAddress,
      POOL_ABI,
      writeContractAsync,
      refetchAllowance,
      refetchTokenBalance
    ]
  );

  const isReady = !!address && !!tokenAddress;

  return {
    sellTokens,
    state,
    tokenBalance,
    allowance,
    isReady,
    paused: pauseStateRef.current
  };
}
